import { Paper, Stack } from "@mui/material";
import { LoadingOrEmptyWrapper } from "components/LoadingAndEmptyHandler";
import { CRREM_DISPLAY_METRIC } from "features/energy-analysis-dashboard/model/constants";
import { useTranslation } from "react-i18next";
import { cn } from "utils/styling";

import { useCrremPathwayChart } from "../hooks/useCrremPathwayChart";
import { CrremPathwayChartCanvas } from "./CrremPathwayChartCanvas";
import { CrremPathwayChartControls } from "./CrremPathwayChartControls";
import { CrremPathwayIncompleteNotice } from "./CrremPathwayIncompleteNotice";

type CrremPathwayChartProps = {
  className?: string;
  projectId: string;
  title: string;
};

export const CrremPathwayChart = ({ className, projectId, title }: CrremPathwayChartProps) => {
  const { t } = useTranslation();
  const chart = useCrremPathwayChart(projectId, t("BuildingProfile.energyAnalysis.unit"));

  return (
    <Paper className={cn("relative p-1", className)}>
      <Stack className="w-full min-h-0 flex-col items-stretch gap-4">
        <h2 className="font-ds-headline-lg text-left text-lds-foreground-heading-active">{title}</h2>
        <CrremPathwayChartControls
          correctionOptions={chart.correctionOptions}
          displayMetric={chart.displayMetric}
          onSave={chart.saveSettings}
          onScenarioChange={chart.setScenarioKey}
          onViewChange={chart.updateView}
          partitionBy={chart.partitionBy}
          scale={chart.scale}
          scenarios={chart.scenarios}
          selectedScenarioKey={chart.selectedScenarioKey}
          settings={chart.settings}
          visibleCorrectionIds={chart.visibleCorrectionIds}
        />
        <CrremPathwayIncompleteNotice missing={chart.missing} />
        <LoadingOrEmptyWrapper height="40rem" showEmpty={chart.isEmpty} showLoading={chart.isLoading}>
          <CrremPathwayChartCanvas
            baseline={chart.baseline}
            labels={{
              baseline: t("BuildingProfile.crremPathway.baseline"),
              pathway: t(
                chart.displayMetric === CRREM_DISPLAY_METRIC.kwh
                  ? "BuildingProfile.crremPathway.targetKwh"
                  : "BuildingProfile.crremPathway.targetCo2"
              ),
              year: t("BuildingProfile.crremPathway.year"),
            }}
            overlayLines={chart.overlayLines}
            partitionBy={chart.partitionBy}
            pathway={chart.pathway}
            stacks={chart.stacks}
            unit={chart.unit}
            years={chart.years}
          />
        </LoadingOrEmptyWrapper>
      </Stack>
    </Paper>
  );
};
